import { useEffect, useRef, useState } from "react";
import type { Touch, TriggerDirection, PairInfo } from "./emailSmsTypes";

/*
    Column widths, in the order they appear: # | Channel | Title | Trigger | slack |
    Status | actions. The slack sits after the trigger so Title and Trigger stay adjacent,
    and the trigger's own fields never wrap.

    Three layouts, chosen from the row's measured width rather than a media query, because
    the row renders both full-width on Configure and narrow inside the Catalog's split view:
      one   everything on a single line
      mid   the send date drops beneath the trigger
      tiny  the trigger moves under the title
*/
const TRIGGER_W = 44 + 28 + 72 + 170 + 3 * 6;
const DATE_W = 160;
/*
    The status column is 150, not 110. The design's own note beside it reads "Status 150
    (fits the approval dropdown, 10/28)" -- at 110 the approval select and the Blocked label
    cannot sit side by side, so the cell wraps them onto two lines and every blocked row
    grows to roughly twice the height the design draws.
*/
const STATUS_W = 150;
const FIXED_W = 24 + 32 + 72 + STATUS_W + 72 + 6 * 8;
const TITLE_W = 220;

type Mode = "one" | "mid" | "tiny";

/*
    The same six values the ad card offers, worded the same way (MPN-2207). A touch is an ad,
    so a touch Ops can only watch while an ad in the same campaign can be moved by hand would
    be an arbitrary difference -- including DO_NOT_USE, which MPN-1612 made reversible.
*/
export const REVIEW_OPTIONS: { value: string; label: string; tone: string }[] = [
  { value: "TODO", label: "To do", tone: "off" },
  { value: "IN_PROGRESS", label: "In progress", tone: "off" },
  { value: "IN_REVIEW", label: "In review", tone: "info" },
  { value: "CHANGES_REQUESTED", label: "Changes requested", tone: "warn" },
  { value: "APPROVED", label: "Approved", tone: "ok" },
  { value: "DO_NOT_USE", label: "Do not use", tone: "bad" },
];

export type TouchRowModel = Touch & {
  n: number;
  pair?: PairInfo;
  dateLabel?: string;
  statusLabel?: string;
  statusTone?: "on" | "off" | "blocked";
  anchorOptions: { v: string; label: string }[];
  hasDays: boolean;
  hasDrag?: boolean;
  hasToggle?: boolean;
  toggleDisabled?: boolean;
  hasStop?: boolean;
  pushedNote?: string;
  /** Amber dot when the catalog moved on after this campaign edited the touch. */
  custStale?: boolean;
  blockHead?: string;
  blockRest?: string;
  blockFix?: string;
  conflictRest?: string;
};

type Props = {
  touch?: TouchRowModel;
  header?: boolean;
  /** Configure shows the resolved send date; the Catalog has no campaign to resolve it. */
  dated?: boolean;
  rounded?: boolean;
  titleEditable?: boolean;
  onTitle?: (value: string) => void;
  onDays?: (value: number) => void;
  onDir?: (value: TriggerDirection) => void;
  onAnchor?: (value: string) => void;
  onToggle?: () => void;
  onPreview?: () => void;
  onMenu?: (at: { x: number; y: number }) => void;
  onCustomized?: (at: { x: number; y: number }) => void;
  onBlockFix?: () => void;
  /** Set this touch's approval status (MPN-2207). Absent in the Catalog and the client
      view, which both show the status as a pill nobody can move. */
  onReviewStatus?: (next: string) => void;
  reviewBusy?: boolean;
};

export function TouchRow({
  touch,
  header = false,
  dated = false,
  rounded = false,
  titleEditable = false,
  onTitle,
  onDays,
  onDir,
  onAnchor,
  onToggle,
  onPreview,
  onMenu,
  onCustomized,
  onBlockFix,
  onReviewStatus,
  reviewBusy = false,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>("one");
  const [hover, setHover] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const check = (width: number) => {
      const base = FIXED_W + TITLE_W + TRIGGER_W;
      const next: Mode =
        width >= base + (dated ? 8 + DATE_W : 0) ? "one" : width >= base ? "mid" : "tiny";
      setMode((prev) => (prev === next ? prev : next));
    };
    check(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => check(entries[0].contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [dated]);

  const tiny = mode === "tiny";
  const triggerCol = TRIGGER_W + (mode === "one" && dated ? 8 + DATE_W : 0);
  const columns = tiny
    ? "32px 72px minmax(0,1fr) 72px"
    : `32px 72px minmax(${TITLE_W}px,360px) ${triggerCol}px minmax(0,1fr) ${STATUS_W}px 72px`;

  if (header) {
    return (
      <div
        ref={rootRef}
        className={`touch-row__head${rounded ? " touch-row__head--rounded" : ""}`}
        style={{ gridTemplateColumns: columns }}
      >
        <span className="touch-row__num-head">#</span>
        <span>Channel</span>
        <span>Title</span>
        {!tiny && (
          <>
            <span>Trigger</span>
            <span />
            <span>Status</span>
          </>
        )}
        <span />
      </div>
    );
  }

  if (!touch) return <div ref={rootRef} />;

  const dateInline = mode === "one" || tiny;
  const dateBelow = mode === "mid" && dated;
  const showHandle = !!touch.hasDrag && hover;
  const paired = !!(touch.pair?.pairStart || touch.pair?.pairEnd) && touch.pair?.pairBand === 1;

  const dateBlock = (
    <>
      {touch.dateLabel && <span className="touch-row__date">→ {touch.dateLabel}</span>}
      {touch.pushedNote && (
        <span className="touch-row__pushed" title={touch.pushedNote} aria-label={touch.pushedNote}>
          <i className="bi bi-moon" />
        </span>
      )}
    </>
  );

  /*
      Each touch carries its own approval status, exactly as an ad does — a touch is
      approved or returned individually. Shown beside the delivery status rather than
      instead of it: "Blocked" and "Changes requested" are different problems.
  */
  const review = touch.reviewStatus;
  const reviewPill =
    review === "CHANGES_REQUESTED"
      ? { label: "Changes requested", tone: "warn" }
      : review === "IN_REVIEW"
        ? { label: "In review", tone: "info" }
        : review === "APPROVED"
          ? { label: "Approved", tone: "ok" }
          : null;

  const reviewValue = REVIEW_OPTIONS.some((option) => option.value === review)
    ? (review as string)
    : "TODO";
  const reviewTone =
    REVIEW_OPTIONS.find((option) => option.value === reviewValue)?.tone ?? "off";

  const statusBlock = (
    <>
      {/* Delivery status first, then the approval control, then Customized -- the design's
          order. Reversed, "To do" sat above "Blocked" and the cell read as a stack of two
          unrelated things rather than a state and the decision on it. */}
      {touch.statusLabel && (
        <span className={`touch-pill touch-pill--${touch.statusTone ?? "off"}`}>
          {touch.statusLabel}
        </span>
      )}
      {onReviewStatus ? (
        <span className={`touch-status touch-status--${reviewTone}`}>
          <span className="touch-status__dot" aria-hidden="true" />
          <select
            className="touch-status__select"
            value={reviewValue}
            disabled={reviewBusy}
            aria-label={`Approval status for ${touch.title}`}
            onChange={(e) => onReviewStatus(e.target.value)}
          >
            {REVIEW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {/* The trigger selects draw their own chevron; this one showed the OS arrow. */}
          <i className="bi bi-chevron-down touch-status__chev" aria-hidden="true" />
        </span>
      ) : (
        reviewPill && (
          <span className={`touch-pill touch-pill--${reviewPill.tone}`}>
            {reviewPill.label}
          </span>
        )
      )}
      {touch.customized && (
        <button
          type="button"
          className="touch-row__cust"
          aria-haspopup="dialog"
          aria-label={
            touch.custStale
              ? `Customized, catalog updated — ${touch.title}. Show what changed`
              : `Customized — ${touch.title}. Show what changed`
          }
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            onCustomized?.({ x: rect.left, y: rect.bottom + 6 });
          }}
        >
          <span
            className={`touch-row__cust-dot${touch.custStale ? " touch-row__cust-dot--stale" : ""}`}
          />
          Customized
        </button>
      )}
    </>
  );

  return (
    <div ref={rootRef} className="touch-row__root">
      <div
        className={`touch-row${touch.on === false ? " touch-row--off" : ""}${
          touch.pair?.pairStart ? " touch-row--pair-start" : ""
        }`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          className={`touch-row__grid${paired ? " touch-row__grid--paired" : ""}${
            tiny || dateBelow ? " touch-row__grid--tall" : ""
          }`}
          style={{ gridTemplateColumns: columns }}
        >
          <span
            className={`touch-row__num${touch.hasDrag ? " touch-row__num--drag" : ""}`}
            title={touch.hasDrag ? "Drag to reorder" : undefined}
          >
            {showHandle ? <i className="bi bi-grip-vertical" aria-hidden="true" /> : touch.n}
          </span>

          <span>
            <span className={`touch-ch touch-ch--${touch.ch}`}>
              {touch.ch === "email" ? "Email" : "SMS"}
            </span>
          </span>

          <div className="touch-row__title-cell">
            {titleEditable ? (
              <input
                className="touch-row__title-input"
                value={touch.title}
                aria-label="Title"
                aria-required="true"
                aria-invalid={!touch.title}
                placeholder="Name this touch"
                onChange={(e) => onTitle?.(e.target.value)}
              />
            ) : (
              <button type="button" className="touch-row__title" title="Preview" onClick={onPreview}>
                {touch.title}
              </button>
            )}
            {touch.hasStop && (
              <span
                className="touch-row__stop"
                title="Reply STOP to opt out. Added by the platform."
                aria-label="Opt-out line added by the platform"
              >
                <i className="bi bi-sign-stop" />
              </span>
            )}
          </div>

          <div className={`touch-row__trigger${tiny ? " touch-row__trigger--wrapped" : ""}`}>
            <span className="touch-row__trigger-fields">
              {touch.hasDays && (
                <>
                  <input
                    type="number"
                    min={0}
                    className="touch-row__days"
                    value={touch.days}
                    aria-label="Days"
                    onChange={(e) => onDays?.(Number(e.target.value))}
                  />
                  <span className="touch-row__days-unit">days</span>
                </>
              )}
              <span className="touch-row__select">
                <select
                  value={touch.dir}
                  aria-label="Before, after or on"
                  onChange={(e) => onDir?.(e.target.value as TriggerDirection)}
                >
                  <option value="before">before</option>
                  <option value="after">after</option>
                  <option value="on">on</option>
                </select>
                <i className="bi bi-chevron-down" />
              </span>
              <span className="touch-row__select touch-row__select--anchor">
                <select
                  value={touch.anchor}
                  aria-label="Key date"
                  onChange={(e) => onAnchor?.(e.target.value)}
                >
                  {touch.anchorOptions.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <i className="bi bi-chevron-down" />
              </span>
            </span>
            {dateInline && dated && <span className="touch-row__date-cell">{dateBlock}</span>}
            {tiny && statusBlock}
          </div>

          {dateBelow && <div className="touch-row__date-below">{dateBlock}</div>}

          {!tiny && <span aria-hidden="true" />}
          {!tiny && <div className="touch-row__status-cell">{statusBlock}</div>}

          <div className={`touch-row__actions${tiny ? " touch-row__actions--tiny" : ""}`}>
            {touch.hasToggle && (
              <button
                type="button"
                role="switch"
                aria-checked={!!touch.on}
                aria-label={`${touch.on ? "Turn off" : "Turn on"} ${touch.title}`}
                disabled={touch.toggleDisabled}
                className={`touch-switch${touch.on ? " touch-switch--on" : ""}`}
                onClick={onToggle}
              >
                <span className="touch-switch__knob" />
              </button>
            )}
            <button
              type="button"
              className="touch-row__menu"
              aria-label={`More for ${touch.title}`}
              aria-haspopup="menu"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                onMenu?.({ x: rect.right, y: rect.bottom + 4 });
              }}
            >
              <i className="bi bi-three-dots" />
            </button>
          </div>
        </div>

        {touch.blockHead && (
          <div className="touch-row__note touch-row__note--warn">
            <span className="touch-row__note-icon">!</span>
            <span className="touch-row__note-text">
              <strong>{touch.blockHead}</strong> {touch.blockRest}
            </span>
            {touch.blockFix && (
              <button type="button" className="touch-row__note-act" onClick={onBlockFix}>
                {touch.blockFix}
              </button>
            )}
          </div>
        )}

        {touch.conflictRest && (
          <div className="touch-row__note touch-row__note--warn">
            <span className="touch-row__note-icon">!</span>
            <span className="touch-row__note-text">
              <strong>Same-day email and text with the same ask. Move one touch.</strong>{" "}
              {touch.conflictRest}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TouchRow;
